import React from 'react'
import ReactDOM from 'react-dom/client'
import ApprovalsWidget from './ApprovalsWidget'
import styles from './styles.css?inline'

// Standalone Approvals SDK. Same partner-facing pattern as OddsWidget /
// DistributionWidget (script tag + init()). Separate UMD bundle so
// partners embedding only the approvals lists don't pay for Tailwind,
// react-query, the bet slip overlay, or the distribution-widget bundle.
//
// Usage on partner site:
//
//   <script src="https://grid.altsportsdata.com/approvals-widget.umd.js"></script>
//   <div id="ad-approvals"></div>
//   <script>
//     ApprovalsWidget.init('#ad-approvals', {
//       sport: 'f1',
//       apiBase: 'https://grid.altsportsdata.com'
//     })
//   </script>
//
// Sportsbook mode — where a book is live (US states, international
// allow-list, and the deny-list regions it is NOT available in):
//
//     ApprovalsWidget.init('#ad-approvals', {
//       sportsbook: 'draftkings',
//       apiBase: 'https://grid.altsportsdata.com'
//     })

export interface ApprovalsWidgetConfig {
  /** Sport slug. One of sport / sportsbook is required. */
  sport?: string
  /** Sportsbook slug (slugified name, e.g. 'draftkings', 'hard-rock-bet'). */
  sportsbook?: string
  apiBase?: string
}

export interface ApprovalsWidgetInstance {
  destroy: () => void
}

const DEFAULT_API_BASE = 'https://grid.altsportsdata.com'

// Inject Poppins from Google Fonts into the host document the first time
// any widget mounts. @font-face declarations don't cross shadow boundaries,
// so the font has to be registered on the page itself for :host to use it.
const POPPINS_HREF = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap'
function ensurePoppinsLoaded(): void {
  if (typeof document === 'undefined') return
  if (document.querySelector(`link[href="${POPPINS_HREF}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = POPPINS_HREF
  document.head.appendChild(link)
}

class ApprovalsWidgetSDK {
  private instances = new Map<HTMLElement, { root: ReactDOM.Root }>()

  init(container: string | HTMLElement, config: ApprovalsWidgetConfig): ApprovalsWidgetInstance {
    const el = typeof container === 'string'
      ? document.querySelector(container) as HTMLElement | null
      : container

    if (!el) {
      throw new Error(`ApprovalsWidget.init: container not found (${String(container)})`)
    }

    ensurePoppinsLoaded()

    const existing = this.instances.get(el)
    if (existing) {
      existing.root.unmount()
      this.instances.delete(el)
    }

    const shadow = el.shadowRoot ?? el.attachShadow({ mode: 'open' })

    if (!shadow.querySelector('style')) {
      const styleEl = document.createElement('style')
      styleEl.textContent = styles
      shadow.appendChild(styleEl)
    }

    const mountPoint = document.createElement('div')
    shadow.appendChild(mountPoint)

    const root = ReactDOM.createRoot(mountPoint)
    root.render(React.createElement(ApprovalsWidget, {
      sport: config.sport,
      sportsbook: config.sportsbook,
      apiBase: config.apiBase ?? DEFAULT_API_BASE,
    }))

    const instance: ApprovalsWidgetInstance = {
      destroy: () => {
        root.unmount()
        this.instances.delete(el)
      },
    }
    this.instances.set(el, { root })
    return instance
  }
}

const sdk = new ApprovalsWidgetSDK()

declare global {
  interface Window {
    ApprovalsWidget: typeof sdk
  }
}

if (typeof window !== 'undefined') {
  window.ApprovalsWidget = sdk
}

export default sdk
